import { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { X, Loader2, MapPin } from 'lucide-react';
import { API } from '../utils/apiConfig';
import { speakPointSet } from '../utils/tts';
import 'leaflet/dist/leaflet.css';

// Fix default Leaflet marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({ click: (e) => onMapClick(e.latlng) });
  return null;
};

const FlyToPoint = ({ position }) => {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 15, { duration: 0.5 });
  }, [position, map]);
  return null;
};

const MapPickerModal = ({ open, onClose, onSelect, label }) => {
  const [position, setPosition] = useState(null);
  const [address, setAddress] = useState('');
  const [addressParts, setAddressParts] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!open) return null;

  const handleMapClick = async (latlng) => {
    setPosition([latlng.lat, latlng.lng]);
    setLoading(true);
    setAddress('');

    try {
      // Reverse geocode via Nominatim
      const resp = await axios.get(
        `https://nominatim.openstreetmap.org/reverse`,
        {
          params: { lat: latlng.lat, lon: latlng.lng, format: 'json', addressdetails: 1 },
          headers: { 'User-Agent': 'VoiceRide/2.0' },
        }
      );
      const data = resp.data;
      const addr = data.address || {};
      const city = addr.city || addr.town || addr.village || addr.municipality || '';
      const road = addr.road || '';
      const house = addr.house_number || '';
      const poi = data.name || '';
      const displayName = poi || (road ? `${road} ${house}, ${city}` : city) || data.display_name || '';

      setAddress(displayName);
      // Store parsed components for the confirm callback
      setAddressParts({ city, road, house_number: house, display_name: displayName });

      // TTS: "Punkt gesetzt: [address]"
      speakPointSet(displayName);
    } catch {
      setAddress(`${latlng.lat.toFixed(4)}, ${latlng.lng.toFixed(4)}`);
      setAddressParts(null);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = () => {
    if (position && address) {
      onSelect({
        lat: position[0],
        lng: position[1],
        display_name: address,
        short_name: address,
        city: addressParts?.city || address.split(',').pop()?.trim() || address,
        road: addressParts?.road || '',
        house_number: addressParts?.house_number || '',
      });
      onClose();
    }
  };

  return (
    <div className="map-picker-overlay" data-testid="map-picker-modal">
      <div className="map-picker-container">
        <div className="map-picker-header">
          <h3><MapPin size={18} /> {label || 'Auf Karte wählen'}</h3>
          <button onClick={onClose} className="map-picker-close" data-testid="map-picker-close">
            <X size={20} />
          </button>
        </div>

        <div className="map-picker-body">
          <MapContainer
            center={[53.55, 10.0]}
            zoom={6}
            style={{ width: '100%', height: '100%' }}
            zoomControl={true}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap'
            />
            <ClickHandler onMapClick={handleMapClick} />
            {position && (
              <>
                <Marker position={position} />
                <FlyToPoint position={position} />
              </>
            )}
          </MapContainer>
        </div>

        <div className="map-picker-footer">
          {loading ? (
            <div className="map-picker-loading">
              <Loader2 size={16} className="spin" /> Adresse wird ermittelt...
            </div>
          ) : address ? (
            <div className="map-picker-address" data-testid="map-picker-address">
              <MapPin size={16} />
              <span>{address}</span>
            </div>
          ) : (
            <div className="map-picker-hint">Tippe auf die Karte, um einen Punkt zu setzen</div>
          )}

          <button
            className="map-picker-confirm"
            onClick={handleConfirm}
            disabled={!position || !address || loading}
            data-testid="map-picker-confirm"
          >
            Übernehmen
          </button>
        </div>
      </div>

      <style>{`
        .map-picker-overlay {
          position: fixed; inset: 0; z-index: 1000;
          background: rgba(0,0,0,0.6); display: flex;
          align-items: center; justify-content: center;
          padding: 1rem;
        }
        .map-picker-container {
          background: white; border-radius: 16px;
          width: 100%; max-width: 700px; height: 80vh;
          display: flex; flex-direction: column;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          overflow: hidden;
        }
        .map-picker-header {
          display: flex; align-items: center; justify-content: space-between;
          padding: 1rem 1.25rem; border-bottom: 1px solid #e2e8f0;
        }
        .map-picker-header h3 {
          display: flex; align-items: center; gap: 0.5rem;
          font-size: 1.05rem; font-weight: 600; margin: 0;
        }
        .map-picker-close {
          background: none; border: none; cursor: pointer;
          color: #64748b; padding: 0.25rem; border-radius: 6px;
        }
        .map-picker-close:hover { background: #f1f5f9; }
        .map-picker-body { flex: 1; min-height: 0; }
        .map-picker-footer {
          padding: 0.75rem 1.25rem; border-top: 1px solid #e2e8f0;
          display: flex; align-items: center; gap: 0.75rem;
        }
        .map-picker-loading, .map-picker-hint {
          flex: 1; font-size: 0.9rem; color: #64748b;
          display: flex; align-items: center; gap: 0.5rem;
        }
        .map-picker-address {
          flex: 1; display: flex; align-items: center; gap: 0.5rem;
          font-size: 0.9rem; color: #0f172a; font-weight: 500;
        }
        .map-picker-confirm {
          padding: 0.6rem 1.25rem; background: #2563eb; color: white;
          border: none; border-radius: 8px; font-weight: 600; cursor: pointer;
          font-size: 0.9rem; white-space: nowrap;
        }
        .map-picker-confirm:disabled { opacity: 0.5; cursor: not-allowed; }
        .map-picker-confirm:hover:not(:disabled) { background: #1d4ed8; }
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { from { transform: rotate(0); } to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default MapPickerModal;
